import type { FCWithChildren } from '@pbk-web/types';
import type { Klass, LexicalNode } from 'lexical';
import type { FC } from 'react';
import { createContext, useContext } from 'react';

import type { EditorFeatures, LexicalProps } from '../../types';
import { useEditorProps } from './EditorPropsContext';

export type FeatureName =
  | 'richText'
  | 'listNumbering'
  | 'links'
  | 'horizontalRule'
  | 'tables'
  | 'images'
  | 'history'
  | 'collaboration'
  | 'multiLanguage'
  | 'singleLine'
  | 'characterLimit'
  | 'dynamicData'
  | 'mentions'
  | 'clearInput'
  | 'autoFocus'
  | 'submit';

/**
 * One editor capability, declared once in `features/registry.ts`.
 * - `isEnabled` is the only place its mode/prop conditions live.
 * - `nodes` are registered on every editor (the config is frozen at mount), enabled or not.
 * - `Provider` wraps the whole editor; `Plugin` renders alongside it.
 */
export type EditorFeature = {
  name: FeatureName;
  isEnabled: (props: LexicalProps) => boolean;
  nodes?: readonly Klass<LexicalNode>[];
  Provider?: FCWithChildren;
  Plugin?: FC;
};

const EnabledFeaturesContext = createContext<ReadonlySet<FeatureName> | null>(null);

/** Holds which features are on for this editor, as decided by the registry. */
export const EnabledFeaturesProvider = EnabledFeaturesContext.Provider;

export const useFeatureEnabled = (name: FeatureName): boolean => {
  const enabled = useContext(EnabledFeaturesContext);
  if (!enabled) throw new Error('useFeatureEnabled must be used within an EnabledFeaturesProvider');
  return enabled.has(name);
};

/** A host-configurable feature's options, or `null` when it's off (including when its mode doesn't apply). */
export const useFeatureConfig = <K extends keyof EditorFeatures & FeatureName>(key: K): NonNullable<EditorFeatures[K]> | null => {
  const { features } = useEditorProps();
  const enabled = useFeatureEnabled(key);
  return enabled ? (features?.[key] ?? null) : null;
};
